import { ChangeDetectorRef, Component, Inject, forwardRef } from '@angular/core';
import { DashboardPanelComponentBase }                      from '@cs/components/shared';
import { DashboardEventHub }                                from '../../dashboard-event-hub.service';
import { DashboardPanelOptions }                            from '../../models/dashboard-panel-options';
import { FilterBarDataSource, FilterBarResultParams, FilterCompareBarQuery, FilterCompareBarService } from '@cs/components/filter-and-compare-bar';
import { dataDescribedToFilterNavbar, FilterBarDataDescribed } from '@cs/components/filter-and-compare-bar';
import { FilterSelectionChangedEventArgs }                  from '@cs/components/advanced-dropdown';
import { ArrayUtils }                                       from '@cs/core';
import { hasPropertyOf }                                    from '@cs/core/utils';

/**
 * Dashboard panel component that renders a local filter bar.
 *
 * Server-side contract:
 *   Panel type = "filter"
 *   Panel data = a DataDescribed payload where each field uses:
 *     - groupId: "filter"      -> rendered as a filter dropdown in the filter bar
 *     - groupId: "navigation"  -> rendered as a navigation-style dropdown
 *   Lookups are resolved from the standard DataDescribed lookups array.
 *
 *   Layout options:
 *     - noBackground: boolean  -> removes the background from the filter bar container
 *
 * When the user changes a filter value the component merges the new
 * values into mainbarApiParams (so they persist in the global store)
 * and then fires triggerNavigation which causes the shell to rebuild
 * the navbar and reload the dashboard. The mainbarApiParams update
 * happens BEFORE triggerNavigation so that getDashboard() can read
 * the filter values from the store when it fires.
 */
@Component({
	selector:    'cs-dashboard-filter',
	standalone:  false,
	templateUrl: './dashboard-filter.component.html'
})
export class DashboardFilterComponent extends DashboardPanelComponentBase<any> {

	name: string;
	filterDataSource: FilterBarDataSource<FilterBarResultParams>;
	noBackground = false;

	private _eventHub: DashboardEventHub;
	private _changeRef: ChangeDetectorRef;
	private _filterCompareBarService: FilterCompareBarService;
	private _filterCompareBarQuery: FilterCompareBarQuery;

	constructor(
		@Inject(forwardRef(() => DashboardEventHub)) eventHub: DashboardEventHub,
		@Inject(DashboardPanelOptions) panelOptions: DashboardPanelOptions,
		changeRef: ChangeDetectorRef,
		filterCompareBarService: FilterCompareBarService,
		filterCompareBarQuery: FilterCompareBarQuery
	) {
		super();
		this._eventHub                = eventHub;
		this._changeRef               = changeRef;
		this._filterCompareBarService = filterCompareBarService;
		this._filterCompareBarQuery   = filterCompareBarQuery;
		this.name                     = panelOptions.name;
	}

	set data(value: any) {
		this._data = value;
		this.dataChanged(value);
	}

	async dataChanged(value: any): Promise<void> {
		if (!value) return;
		this.parseLayout(value);
		await this.buildFilterDataSource(value);
	}

	async update(data: any): Promise<void> {
		if (this._data && ArrayUtils.isEqual(this._data, data)) return;
		this._data = data;
		this.parseLayout(data);
		await this.buildFilterDataSource(data);
	}

	onFilterSelectionChanged(event: FilterSelectionChangedEventArgs): void {
		// Pre-merge the filter values into mainbarApiParams so that when
		// getDashboard() fires (after the navbar round-trip), it can read
		// them from the store immediately — avoiding the race condition
		// where setMainbar() runs after getDashboard().
		const currentState = this._filterCompareBarQuery.getValue();
		const updatedApiParams = {
			...(currentState.mainbarApiParams || {}),
			...event.newApiParams
		};

		this._filterCompareBarService.update({
			mainbarApiParams: updatedApiParams
		});

		// Now trigger the standard navbar round-trip which will rebuild
		// the navbar and reload the dashboard.
		this._filterCompareBarService.triggerNavigation(event.newApiParams);
	}

	private parseLayout(data: any): void {
		if (hasPropertyOf(data, 'layout') && data.layout) {
			this.noBackground = data.layout.noBackground === true;
		} else {
			this.noBackground = false;
		}
	}

	private async buildFilterDataSource(data: any): Promise<void> {
		try {
			const described       = new FilterBarDataDescribed(data);
			this.filterDataSource = await dataDescribedToFilterNavbar<FilterBarResultParams>(described);
			this._changeRef.detectChanges();
		} catch (e) {
			// Fallback: if conversion fails, render nothing rather than break the dashboard
			console.error('DashboardFilterComponent: Failed to build filter data source', e);
			this.filterDataSource = null;
		}
	}
}
